import { useAuth } from "../../src/Context/AuthContext";
import { useNavigate, useLocation } from "react-router-dom";
import OverlaySwitcher from "./OverlaySwitcher";
import { useState, useEffect } from "react";

const canSeeLink = (linkType, role) => {
    if (role === "1" || role === "2") return true;

    switch (linkType) {
        case "Agent":
            return ["1", "2", "3"].includes(role);
        case "Admin":
            return ["1", "2"].includes(role);
        case "Policy":
            return ["1", "2", "4", "5"].includes(role);
        default:
            return false;
    }
};

export default function Navbar({ portalSettings }) {
    const { isAuthenticated, user, logout, loading } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    const [hamburgerOpen, setHamburgerOpen] = useState(false);
    const [externalLinks, setExternalLinks] = useState([]);
    const [externalOpen, setExternalOpen] = useState(false);
    const [underwritingOpen, setUnderwritingOpen] = useState(false);

    const apiURL = import.meta.env.VITE_API;

    if (loading || !portalSettings) return null;
    const psm = portalSettings || {};

    const agentMatch = location.pathname.match(/\/Agent\/([^/]+)/i);
    const agentCodeFromUrl = agentMatch ? agentMatch[1] : null;

    const canShowFireLight =
        isAuthenticated &&
        ["1", "2", "3"].includes(user?.role) &&
        Boolean(agentCodeFromUrl) &&
        (psm.hasFireLight);

    const canShowICover =
        isAuthenticated &&
        ["1", "2", "3"].includes(user?.role) &&
        Boolean(agentCodeFromUrl) &&
        (psm.hasICover);

    const canShowImages =
        (user?.role === "1" || user?.role === "2") &&
        user?.hasImage === "true" &&
        psm.hasImagePortal;

    const canShowPolicyApproval =
        (user?.role === "1" || user?.role === "2") &&
        user?.hasUW === "true" &&
        psm.hasUnderwritingPortal;

    const canShowRetentionLimit =
        (user?.role === "1" || user?.role === "2") &&
        user?.hasUW === "true" &&
        psm.hasUnderwritingPortal;

    const canShowUnderwritingDropdown =
        canShowPolicyApproval || canShowRetentionLimit;

    const links = [
        {
            label: "Policy List",
            path: "/Policies",
            show: (user?.role === "4" || user?.role === "5") && psm.hasPolicyPortal
        },
        {
            label: "Agent",
            path: `/Agent/${user?.agentCode}`,
            show: user?.role === "3" && psm.hasAgentPortal
        },
        {
            label: "Agent",
            path: `/TermAgent/${user?.agentCode}`,
            show: user?.role === "6" && psm.hasAgentPortal
        },
        {
            label: "Admin",
            path: "/Admin",
            show: user?.role === "1" || (user?.role === "2" && (psm.hasPolicyPortal || psm.hasAgentPortal))
        },
        {
            label: "Images",
            path: "/Images",
            show: canShowImages
        },
        {
            label: "Provider",
            path: "/Provider",
            show:
                (user?.role === "1" || user?.role === "2") &&
                psm.hasProviderPortal
        },
        {
            label: "Profile",
            path: "/Profile",
            show: isAuthenticated
        }
    ];

    useEffect(() => {
        if (!isAuthenticated || !user) {
            setExternalLinks([]);
            return;
        }

        const loadExternalLinks = async () => {
            try {
                const res = await fetch(`${apiURL}/shared/GetExternalLinks`, {
                    method: "GET",
                    credentials: "include"
                });

                if (res.status === 401) {
                    logout();
                    navigate("/login");
                    return;
                }

                if (res.status === 403) {
                    navigate("/home");
                    return;
                }

                if (!res.ok) {
                    throw new Error("Failed to load external links");
                }

                const data = await res.json();
                const filtered = data.filter(link =>
                    canSeeLink(link.linkType, user.role)
                );

                setExternalLinks(filtered);
            } catch (err) {
                console.error("Error loading external links", err);
            }
        };

        loadExternalLinks();
    }, [isAuthenticated, user, apiURL, logout, navigate]);

    const visibleLinks = links.filter(l => l.show);

    const hideAuthActions =
        location.pathname.toLowerCase().startsWith("/provider") &&
        !isAuthenticated;

    const handleEAppClick = async () => {
        if (!agentCodeFromUrl) {
            alert("You must be on an Agent page to use this link.");
            return;
        }

        try {
            const res = await fetch(
                `${apiURL}/agent/FireLightEAppLogin?agentCode=${agentCodeFromUrl}`,
                {
                    method: "POST",
                    credentials: "include"
                }
            );

            if (!res.ok) throw new Error("Failed to get SAML from API");

            const html = await res.text();
            const newWindow = window.open("", "_blank");
            if (!newWindow) throw new Error("Popup blocked");

            newWindow.document.write(html);
            newWindow.document.close();

            const form = newWindow.document.querySelector("form");
            if (form) form.submit();
        } catch (err) {
            console.error("Error opening EApp:", err);
        }
    };

    const handleFireLightClick = async () => {
        if (!agentCodeFromUrl) {
            alert("You must be on an Agent page to use this link.");
            return;
        }

        try {
            const res = await fetch(
                `${apiURL}/agent/FireLightEAppLogin?agentCode=${agentCodeFromUrl}`,
                {
                    method: "POST",
                    credentials: "include"
                }
            );

            if (!res.ok) throw new Error("Failed to get SAML from API");

            const html = await res.text();
            const newWindow = window.open("", "_blank");
            if (!newWindow) throw new Error("Popup blocked");

            newWindow.document.write(html);
            newWindow.document.close();

            const form = newWindow.document.querySelector("form");
            if (form) form.submit();
        } catch (err) {
            console.error("Error opening EApp:", err);
        }
    };

    const handleICoverClick = async () => {
        if (!agentCodeFromUrl) {
            alert("You must be on an Agent page to use this link.");
            return;
        }

        try {
            const res = await fetch(
                `${apiURL}/agent/ICoverLogin?agentCode=${agentCodeFromUrl}`,
                {
                    method: "POST",
                    credentials: "include"
                }
            );

            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                alert(errorData.message || 'iCover Redirect Failed.');
                return;
            }

            const data = await res.json();

            if (data.redirectLink) {
                window.open(data.redirectLink, '_blank', 'noopener,noreferrer');
                return;
            }

            alert('Missing iCover redirect response.');
        } catch (err) {
            console.error("Error redirecting to iCover:", err);
        }
    };

    const isUnderwritingActive =
        location.pathname.startsWith("/Underwriting") ||
        location.pathname.startsWith("/RetentionLimit");

    return (
        <>
            <nav className="navbar">
                <img
                    src={`https://insuranceservices.actmanre.com/AMRImages/${psm.groupCode}/${psm.companyLogo}`}
                    alt="Company Logo"
                    className="navbar-logo"
                    style={{ cursor: "pointer" }}
                    onClick={() => navigate("/home")}
                />

                <div className="navbar-links">
                    {visibleLinks.map(link => {
                        const isActive =
                            link.path &&
                            location.pathname.startsWith(link.path);

                        return (
                            <span
                                key={link.label}
                                className={`nav-link ${isActive ? "active" : ""}`}
                                onClick={() => navigate(link.path)}
                            >
                                {link.label}
                            </span>
                        );
                    })}

                    {canShowUnderwritingDropdown && (
                        <div
                            className="nav-dropdown"
                            onMouseEnter={() => setUnderwritingOpen(true)}
                            onMouseLeave={() => setUnderwritingOpen(false)}
                        >
                            <span className={`nav-link ${isUnderwritingActive ? "active" : ""}`}>
                                Underwriting
                            </span>

                            {underwritingOpen && (
                                <div className="dropdown-menu">
                                    {canShowPolicyApproval && (
                                        <div
                                            className="dropdown-item"
                                            onClick={() => navigate("/Underwriting")}
                                        >
                                            Policy Approval
                                        </div>
                                    )}

                                    {canShowRetentionLimit && (
                                        <div
                                            className="dropdown-item"
                                            onClick={() => navigate("/RetentionLimit")}
                                        >
                                            Retention Limit
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {externalLinks.length > 0 && (
                        <div
                            className="nav-dropdown"
                            onMouseEnter={() => setExternalOpen(true)}
                            onMouseLeave={() => setExternalOpen(false)}
                        >
                            <span className="nav-link">
                                External Links
                            </span>

                            {externalOpen && (
                                <div className="dropdown-menu">
                                    {externalLinks.map(link => (
                                        <div
                                            key={link.linkName}
                                            className="dropdown-item"
                                            onClick={() =>
                                                window.open(
                                                    link.linkURL,
                                                    "_blank",
                                                    "noopener,noreferrer"
                                                )
                                            }
                                        >
                                            {link.linkName}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {canShowFireLight && (
                        <span className="nav-link" onClick={handleFireLightClick}>
                            EApp
                        </span>
                    )}
                    {canShowICover && (
                        <span className="nav-link" onClick={handleICoverClick}>
                            Access E-App
                        </span>
                    )}
                </div>

                {!hideAuthActions && (
                    <div className="navbar-actions">
                        {isAuthenticated ? (
                            <>
                                <span>Hi, {user?.username}</span>
                                <button
                                    className="baseButton"
                                    onClick={logout}
                                >
                                    Logout
                                </button>
                            </>
                        ) : (
                            <button onClick={() => navigate("/login")}>
                                Login
                            </button>
                        )}
                    </div>
                )}

                <div className="navbar-ham">
                    <button onClick={() => setHamburgerOpen(p => !p)}>
                        &#9776;
                    </button>

                    {hamburgerOpen && (
                        <div className="hamburger-dropdown">
                            {visibleLinks.map(link => (
                                <span
                                    key={link.label}
                                    className="nav-link"
                                    onClick={() => {
                                        navigate(link.path);
                                        setHamburgerOpen(false);
                                    }}
                                >
                                    {link.label}
                                </span>
                            ))}

                            {canShowUnderwritingDropdown && (
                                <>
                                    <span className="nav-link">Underwriting</span>

                                    {canShowPolicyApproval && (
                                        <span
                                            className="nav-link"
                                            onClick={() => {
                                                navigate("/Underwriting");
                                                setHamburgerOpen(false);
                                            }}
                                        >
                                            Policy Approval
                                        </span>
                                    )}

                                    {canShowRetentionLimit && (
                                        <span
                                            className="nav-link"
                                            onClick={() => {
                                                navigate("/RetentionLimit");
                                                setHamburgerOpen(false);
                                            }}
                                        >
                                            Retention Limit
                                        </span>
                                    )}
                                </>
                            )}

                            {externalLinks.length > 0 && (
                                <>
                                    <span className="nav-link">External Links</span>
                                    {externalLinks.map(link => (
                                        <span
                                            key={link.linkName}
                                            className="nav-link"
                                            onClick={() => {
                                                window.open(
                                                    link.linkURL,
                                                    "_blank",
                                                    "noopener,noreferrer"
                                                );
                                                setHamburgerOpen(false);
                                            }}
                                        >
                                            {link.linkName}
                                        </span>
                                    ))}
                                </>
                            )}

                            {canShowFireLight && (
                                <span
                                    className="nav-link"
                                    onClick={() => {
                                        handleFireLightClick();
                                        setHamburgerOpen(false);
                                    }}
                                >
                                    EApp
                                </span>
                            )}

                            {canShowICover && (
                                <span
                                    className="nav-link"
                                    onClick={() => {
                                        handleICoverClick();
                                        setHamburgerOpen(false);
                                    }}
                                >
                                    EApp
                                </span>
                            )}

                            {!hideAuthActions &&
                                (isAuthenticated ? (
                                    <span
                                        onClick={() => {
                                            logout();
                                            setHamburgerOpen(false);
                                        }}
                                    >
                                        Logout
                                    </span>
                                ) : (
                                    <span
                                        onClick={() => {
                                            navigate("/login");
                                            setHamburgerOpen(false);
                                        }}
                                    >
                                        Login
                                    </span>
                                ))}
                        </div>
                    )}
                </div>
            </nav>

            <OverlaySwitcher />
        </>
    );
}